import { css } from '@emotion/react';
import { IonCol, IonRow, IonSelect, IonSelectOption } from '@ionic/react';
import React from 'react';
import { useGetDatasourceMeasurements } from '../../api/queries/getDatasourceMeasurements';
import { AQIHistoryChart } from '../../components/AQIHistoryChart';
import ChangeInLastHour from '../../components/ChangeInLastHour';
import { useSettings } from '../../contexts/SettingsContext';
import { processMeasurements, SeriesLabel } from '../../shared/measurements';
import * as commonCss from '../../styles/common';
import {
  AirQualityMarkerDto,
  AqiLevelDto,
  AqiStandardDto,
  DatasourceId,
  DatasourceMeasurementsDto,
  NetworkId,
  Pollutant,
  PollutantConfigDto,
} from '../../types';
import { BaseSection } from '../BaseSection';
import { SkeletonText } from '../../components/SkeletonText';
import { useQueryClient } from '@tanstack/react-query';

export const MeasurementsSection: React.FC = () => {
  const { marker, network, aqiMetricKeys, aqiStandard, aqiLevel, datasourcePollutantInfos } = useSettings();

  return (
    network &&
    marker &&
    aqiMetricKeys &&
    aqiStandard &&
    aqiLevel &&
    datasourcePollutantInfos && (
      <MeasurementsDataFetcher
        networkId={network.networkId}
        datasourceId={marker.datasourceId}
        pollutantConfigs={network.pollutantConfigs}
        aqiStandard={aqiStandard}
        aqiLevel={aqiLevel}
        mainPollutant={datasourcePollutantInfos[0].pollutant}
        marker={marker}
      />
    )
  );
};

const MeasurementsDataFetcher: React.FC<{
  networkId: NetworkId;
  datasourceId: DatasourceId;
  pollutantConfigs: PollutantConfigDto[];
  aqiStandard: AqiStandardDto;
  aqiLevel: AqiLevelDto;
  mainPollutant: Pollutant;
  marker: AirQualityMarkerDto;
}> = ({ networkId, datasourceId, pollutantConfigs, aqiStandard, aqiLevel, mainPollutant, marker }) => {
  const queryClient = useQueryClient();
  const { data, isFetching } = useGetDatasourceMeasurements({
    networkId,
    datasourceId,
  });

  // Refetch air-quality markers when we notice that we have a more recent hour
  // than the most recent hour in the markers data.
  React.useEffect(() => {
    if (!data || !marker) {
      return;
    }

    const hasLastValue = Object.values(data.measurements.measurements).some((series) => series.at(-1)! !== null);
    const lastDataHour = data.measurements.times.at(-1)!;
    const markerSampledAt = marker.sampledAt;

    if (hasLastValue && lastDataHour > markerSampledAt) {
      queryClient.invalidateQueries({ queryKey: ['air-quality-markers', networkId] });
    }
  }, [marker, data, queryClient]);

  return (
    data && (
      <MeasurementsSectionContent
        data={data.measurements}
        isFetching={isFetching}
        pollutantConfigs={pollutantConfigs}
        aqiStandard={aqiStandard}
        mainPollutant={mainPollutant}
        aqiLevel={aqiLevel}
      />
    )
  );
};

const MeasurementsSectionContent: React.FC<{
  data: DatasourceMeasurementsDto;
  isFetching: boolean;
  pollutantConfigs: PollutantConfigDto[];
  aqiStandard: AqiStandardDto;
  mainPollutant: Pollutant;
  aqiLevel: AqiLevelDto;
}> = ({ data, isFetching, pollutantConfigs, aqiStandard, mainPollutant, aqiLevel }) => {
  const measurementSectionCss = {
    centeredCol: css({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }),
  };

  const processedMeasurements = React.useMemo(
    () => processMeasurements(data, pollutantConfigs, aqiStandard),
    [data, pollutantConfigs, aqiStandard]
  );

  const [chartOption, setChartOption] = React.useState<SeriesLabel | undefined>();

  // Set the default chart option to the main pollutant whenever data changes
  React.useEffect(() => {
    setChartOption(processedMeasurements.getSeriesByPollutant(mainPollutant).seriesLabel);
  }, [processedMeasurements, mainPollutant]);

  return (
    <BaseSection title="Trend">
      <IonCol>
        <IonRow>
          {/* Pollutant selector */}
          <IonCol>
            <IonSelect
              interface="popover"
              value={chartOption}
              onIonChange={(e) => {
                const selectedOption = processedMeasurements.getSeriesByLabel(e.detail.value);
                if (selectedOption) {
                  setChartOption(selectedOption.seriesLabel);
                }
              }}
            >
              {processedMeasurements.seriesLabels.map((label) => (
                <IonSelectOption key={label} value={label} disabled={!processedMeasurements.isAvailable(label)}>
                  {label}
                </IonSelectOption>
              ))}
            </IonSelect>
          </IonCol>
        </IonRow>
        <IonRow>
          {/* Change Text */}
          <IonCol css={measurementSectionCss.centeredCol}>
            {isFetching || !processedMeasurements || !chartOption ? (
              <SkeletonText height="1rem" width="60%" />
            ) : (
              <ChangeInLastHour data={processedMeasurements.getSeriesByLabel(chartOption)} />
            )}
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol css={commonCss.centered}>
            {/* Chart */}
            {chartOption && (
              <AQIHistoryChart
                aqiStandard={aqiStandard}
                data={processedMeasurements.getSeriesByLabel(chartOption)}
                times={processedMeasurements.times}
                chartHeight={200}
                aqiLevel={aqiLevel}
                isLoading={isFetching}
              />
            )}
          </IonCol>
        </IonRow>
      </IonCol>
    </BaseSection>
  );
};
